import Capabilities, { CapabilitiesImpl } from '@webgl/core/Capabilities'
import { compileProgram, CreateProgram } from '@webgl/core/CreateProgram'
import { GlslModule } from '@webgl/glsl/glslModule'
import Program from 'nanogl/program'
import { GLContext } from 'nanogl/types'

interface ModuleImportInterface {
  default: object
}

type ProgramSource = {
  id: string
  fragModuleId: string | null
  vertModuleId: string | null
  vert: GlslModule | null
  frag: GlslModule | null
}

const _sources: ProgramSource[] = []

function getPair(id: string): ProgramSource {
  let res = _sources.find((p) => p.id === id)
  if (res === undefined) {
    res = {
      id,
      vert: null,
      frag: null,
      vertModuleId: null,
      fragModuleId: null,
    }
    _sources.push(res)
  }
  return res
}

const modules = import.meta.glob('./**/shader.{vert,frag}', { eager: true })

Object.keys(modules).forEach((k: string) => {
  const filepath = k.substring(2, k.length)
  const programId = filepath.split('/')[0]

  const pair = getPair(programId)

  if (filepath.endsWith('frag')) {
    pair.fragModuleId = k
    pair.frag = (modules[k] as ModuleImportInterface).default as GlslModule
  } else {
    pair.vertModuleId = k
    pair.vert = (modules[k] as ModuleImportInterface).default as GlslModule
  }
})

for (const pair of _sources) {
  if (pair.vert === null) {
    throw `Program ${pair.id} missing vertex shader`
  }

  if (pair.frag === null) {
    throw `Program ${pair.id} missing fragment shader`
  }
}

type PrecisionEnum = 'lowp' | 'mediump' | 'highp'

export class ProgramsLib {
  private _programs: Map<string, Program>

  highp: PrecisionEnum = 'lowp'
  mediump: PrecisionEnum = 'lowp'

  version: string
  capabilities: CapabilitiesImpl

  constructor(private gl: GLContext) {
    this.capabilities = Capabilities(gl)

    this.mediump = this.capabilities.hasMediumpPrecision ? 'mediump' : 'lowp'
    this.highp = this.capabilities.hasHighpPrecision ? 'highp' : this.mediump
    this.version = `#version ${this.capabilities.isWebgl2 ? '300 es' : '100'}`

    this._programs = new Map()

    for (const source of _sources) {
      const p = compileProgram(gl, new Program(gl), source.vert(this), source.frag(this), undefined, source.id)

      /// #if _DEBUG
      this._handleHMR(source, p)
      /// #endif

      this._programs.set(source.id, p)
    }
  }

  create(vert: GlslModule, frag: GlslModule, prefix?: string): Program {
    return CreateProgram(this.gl, vert, frag, prefix, this)
  }

  geSourceById(id: string): ProgramSource {
    const source = _sources.find((s) => s.id === id)
    if (source === undefined) {
      throw `Program "${id}" not found`
    }
    return source
  }

  get(id: string): Program {
    if (!this._programs.has(id)) {
      const source = this.geSourceById(id)
      const p = compileProgram(
        this.gl,
        new Program(this.gl),
        source.vert!(this),
        source.frag!(this),
        undefined,
        source.id
      )
      this._programs.set(source.id, p)
      return p
    }
    return this._programs.get(id)!
  }

  /// #if _DEBUG
  _handleHMR(source: ProgramSource, p: Program): void {
    source.vert.onHmr((s) => {
      if (this._validate(s(this), source.frag(this))) {
        source.vert = s
        p.compile(source.vert(this), source.frag(this))
      }
    })
    source.frag.onHmr((s) => {
      if (this._validate(source.vert(this), s(this))) {
        source.frag = s
        p.compile(source.vert(this), source.frag(this))
      }
    })
  }

  private _validate(v: string, f: string): boolean {
    const p = new Program(this.gl)
    return p.compile(v, f)
  }

  /// #endif
}

const _instances = new WeakMap<GLContext, ProgramsLib>()

export default function Programs(gl: GLContext): ProgramsLib {
  let res = _instances.get(gl)
  if (!res) {
    res = new ProgramsLib(gl)
    _instances.set(gl, res)
  }
  return res
}

// Vite HMR handling
if (import.meta.hot) {
  import.meta.hot.accept((newModule) => {
    // Handle module updates
    console.log('HMR update detected for ProgramsLib')
  })
}
